import { useMutation, useQuery } from '@tanstack/react-query'

import { TeamRole } from '@pkg/types/team'

import api from '@/api'
import { queryClient } from '@/react-query/client'
import { TEAM_KEY } from '@/react-query/team'
import { TEAM_MATES_KEY } from '@/react-query/team-members'

export const SENT_TEAM_INVITES_KEY = 'sentTeamInvites'
export const TEAM_INVITE_KEY = 'teamInvite'
export const RECEIVED_TEAM_INVITES_KEY = 'receivedTeamInvites'

export const useTeamInvite = (teamInviteId: string) => {
  return useQuery({
    queryKey: [TEAM_INVITE_KEY, teamInviteId],
    queryFn: async () =>
      (await api.getTeamInvite(teamInviteId)).data.teamInvite,
  })
}

export const useAllSentTeamInvites = () => {
  return useQuery({
    queryKey: [SENT_TEAM_INVITES_KEY],
    queryFn: async () => (await api.getAllSentTeamInvites()).data.teamInvites,
  })
}

export const useAllReceivedTeamInvites = () => {
  return useQuery({
    queryKey: [RECEIVED_TEAM_INVITES_KEY],
    queryFn: async () =>
      (await api.getAllReceivedTeamInvites()).data.teamInvites,
  })
}

export const useSendTeamInvite = () => {
  return useMutation({
    mutationFn: async ({
      toEmail,
      role,
    }: {
      toEmail: string
      role: TeamRole
    }) =>
      await api.sendTeamInvite({
        toEmail,
        role,
      }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [SENT_TEAM_INVITES_KEY],
      }),
  })
}

export const useCancelTeamInvite = (teamInviteId: string) => {
  return useMutation({
    mutationFn: async () => await api.cancelTeamInvite(teamInviteId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SENT_TEAM_INVITES_KEY],
      })
    },
  })
}

export const useAcceptTeamInvite = (teamInviteId: string) => {
  return useMutation({
    mutationFn: async () => await api.acceptTeamInvite(teamInviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RECEIVED_TEAM_INVITES_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [TEAM_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [TEAM_MATES_KEY],
      })
      queryClient.invalidateQueries({
        queryKey: [SENT_TEAM_INVITES_KEY],
      })
    },
  })
}

export const useDeclineTeamInvite = (teamInviteId: string) => {
  return useMutation({
    mutationFn: async () => await api.declineTeamInvite(teamInviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [RECEIVED_TEAM_INVITES_KEY],
      })
    },
  })
}

export const useUpdateTeamInviteRole = () => {
  return useMutation({
    mutationFn: async ({
      teamInviteId,
      role,
    }: {
      teamInviteId: string
      role: TeamRole
    }) =>
      await api.updateTeamInviteRole({
        teamInviteId,
        role,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [SENT_TEAM_INVITES_KEY],
      })
    },
  })
}
